import React from "react";
import { Tool, useToggleFavoriteTool, useDeleteTool, useSetActiveToolId } from "@/lib/tools-store";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Edit3, Copy, Trash2, MoreHorizontal, Link, Activity, Zap, Clock, Star } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface ToolCardProps {
  tool: Tool;
  onEdit: (tool: Tool) => void;
}
export function ToolCard({ tool, onEdit }: ToolCardProps) {
  const toggleFavorite = useToggleFavoriteTool();
  const deleteTool = useDeleteTool();
  const setActiveToolId = useSetActiveToolId();
  const handleTest = () => {
    setActiveToolId(tool.id);
    toast.info(`Opening playground for ${tool.name}`);
  };
  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(tool.id);
  };
  const providerColors = {
    'Built-in': 'bg-primary/10 text-primary border-primary/20',
    'Custom': 'bg-chart-4/10 text-chart-4 border-chart-4/20',
    'Marketplace': 'bg-chart-2/10 text-chart-2 border-chart-2/20',
  };
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card className="group relative flex flex-col h-full overflow-hidden border-white/30 bg-card/60 backdrop-blur-xl hover:border-primary/40 hover:shadow-glow transition-all">
        <div className="p-5 space-y-4 flex-grow">
          <div className="flex justify-between items-start">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center border border-white/10 shrink-0 overflow-hidden">
                <img src={tool.iconUrl} alt={tool.name} className="w-6 h-6 object-contain" />
              </div>
              <div>
                <h3 className="font-bold text-sm leading-snug truncate max-w-[150px]">{tool.name}</h3>
                <Badge variant="outline" className={`text-[9px] mt-1 uppercase tracking-tighter ${providerColors[tool.provider]}`}>
                  {tool.provider}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleFavorite}>
                <Star className={`w-3.5 h-3.5 ${tool.isFavorite ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(tool)} className="gap-2"><Edit3 className="w-4 h-4" /> Edit</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2"><Copy className="w-4 h-4" /> Clone</DropdownMenuItem>
                  <DropdownMenuItem className="gap-2"><Link className="w-4 h-4" /> Connect</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => deleteTool(tool.id)} className="text-destructive gap-2"><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 h-[32px]">
            {tool.description}
          </p>
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary" className="text-[10px] bg-muted/50">{tool.category}</Badge>
            <Badge variant="outline" className={`text-[10px] ${tool.status === 'connected' ? 'text-primary border-primary/20' : 'text-destructive border-destructive/20'}`}>
              {tool.status}
            </Badge>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-white/5 border-t border-white/5">
          <div className="p-3 text-center bg-card/20">
            <div className="flex items-center justify-center gap-1 mb-1">
               <Activity className="w-3 h-3 text-chart-3" />
               <p className="text-[9px] text-muted-foreground uppercase font-semibold">Calls</p>
            </div>
            <p className="text-xs font-bold">{tool.metrics.calls.toLocaleString()}</p>
          </div>
          <div className="p-3 text-center bg-card/20 border-l border-white/5">
            <div className="flex items-center justify-center gap-1 mb-1">
               <Zap className="w-3 h-3 text-primary" />
               <p className="text-[9px] text-muted-foreground uppercase font-semibold">Success</p>
            </div>
            <p className="text-xs font-bold">{tool.metrics.successRate}%</p>
          </div>
          <div className="p-3 text-center bg-card/20 border-t border-white/5">
            <div className="flex items-center justify-center gap-1 mb-1">
               <Clock className="w-3 h-3 text-amber-500" />
               <p className="text-[9px] text-muted-foreground uppercase font-semibold">Latency</p>
            </div>
            <p className="text-xs font-bold">{tool.metrics.latency}ms</p>
          </div>
          <div className="p-3 text-center bg-card/20 border-t border-l border-white/5">
            <div className="flex items-center justify-center gap-1 mb-1">
               <p className="text-[9px] text-muted-foreground uppercase font-semibold">Avg Cost</p>
            </div>
            <p className="text-xs font-bold">${tool.metrics.avgCost}</p>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-[2px]">
          <Button size="sm" className="rounded-full bg-white text-black hover:bg-white/90 gap-2 font-bold" onClick={handleTest}>
            <Play className="w-3.5 h-3.5 fill-current" />
            Test Tool
          </Button>
        </div>
      </Card>
    </motion.div>
  );
}
